import {inject} from '@angular/core';
import {CanActivateChildFn, RedirectFunction, Router} from '@angular/router';
import {BookingDraftRoute, FormSaverFacadeService,} from '../services/comeback-link/form-saver-facade.service';

const bookingChildRoutes = new Set<BookingDraftRoute>([
  '/select-property',
  '/service-provider',
  '/property-owner',
  '/power-of-attorney',
  '/provider-power-of-attorney',
  '/inheritance-documents',
  '/checkout',
]);

/**
 * Sends root navigations to the first booking route that still needs user input.
 */
export const bookingEntryRedirect: RedirectFunction = ({fragment, queryParams}) => {
  const formSaver = inject(FormSaverFacadeService);
  const router = inject(Router);

  formSaver.restoreLocalOnce();

  return router.createUrlTree([formSaver.getFirstInvalidBookingRoute()], {
    fragment: fragment ?? undefined,
    queryParams,
  });
};

/**
 * Prevents manual URL navigation to booking steps that are not reachable yet.
 */
export const bookingFlowGuard: CanActivateChildFn = (route) => {
  const formSaver = inject(FormSaverFacadeService);
  const router = inject(Router);
  const requestedRoute = `/${route.routeConfig?.path ?? ''}` as BookingDraftRoute;

  // The parent route also hosts non-booking children, so only guard known booking steps.
  if (!bookingChildRoutes.has(requestedRoute)) {
    return true;
  }

  // A direct browser navigation starts with an empty in-memory form; restore the draft first.
  formSaver.restoreLocalOnce();

  const firstInvalidRoute = formSaver.getFirstInvalidBookingRoute();

  const requestedStep = formSaver.getBookingRouteStep(requestedRoute);
  const firstInvalidStep = formSaver.getBookingRouteStep(firstInvalidRoute);
  const isInheritanceStepUnavailable =
    requestedRoute === '/inheritance-documents' && !formSaver.hasInheritanceDocumentationStep();

  // Users may revisit earlier steps, but cannot jump past the first incomplete step.
  if (isInheritanceStepUnavailable || requestedStep > firstInvalidStep) {
    return router.createUrlTree([firstInvalidRoute]);
  }

  return true;
};
